import apiClient from '../client'
import { buildJsonApiQuery } from '@/utils/jsonapi'

/**
 * Categories API Module
 * Returns raw JSON:API responses - normalization happens in Redux thunks
 */

/**
 * Fetch all categories for current tenant
 * @param {Object} [queryOptions] - JSON:API query options
 * @param {Object} [queryOptions.filters] - Filter criteria (e.g., { name: { icontains: 'worship' } })
 * @param {Array<string>|string} [queryOptions.sort] - Sort fields (e.g., ['name'])
 * @param {Array<string>|string} [queryOptions.include] - Relations to include (e.g., ['parent'])
 * @param {number} [queryOptions.page] - Page number
 * @param {number} [queryOptions.pageSize] - Items per page
 * @returns {Promise<Object>} JSON:API response with data, meta, links
 */
export async function fetchCategories(queryOptions = {}) {
  const params = buildJsonApiQuery(queryOptions)
  const response = await apiClient.get('categories/', { params })
  return response.data
}

/**
 * Create a new category
 * @param {Object} data - Category data {name, description, color}
 * @returns {Promise<Object>} JSON:API response with created category
 */
export async function createCategory(data) {
  const response = await apiClient.post('categories/', data)
  return response.data
}

/**
 * Update an existing category
 * @param {string} id - Category UUID
 * @param {Object} data - Updated category data
 * @returns {Promise<Object>} JSON:API response with updated category
 */
export async function updateCategory(id, data) {
  const response = await apiClient.patch(`categories/${id}/`, data)
  return response.data
}

/**
 * Delete a category
 * @param {string} id - Category UUID
 * @returns {Promise<void>}
 */
export async function deleteCategory(id) {
  await apiClient.delete(`categories/${id}/`)
}

/**
 * Reorder categories
 * @param {Array<Object>} orderedCategories - Array of {id, display_order}
 * @returns {Promise<Object>} JSON:API response with updated categories
 */
export async function reorderCategories(orderedCategories) {
  const response = await apiClient.post('categories/reorder/', {
    categories: orderedCategories
  })
  return response.data
}
